import { Helmet } from "react-helmet";

interface LocalBusinessSchemaProps {
  pageName?: string;
  pageDescription?: string;
  pageUrl?: string;
}

export const LocalBusinessSchema = ({ pageName, pageDescription, pageUrl }: LocalBusinessSchemaProps) => {
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "HomeAndConstructionBusiness",
    "@id": "https://smartliftgaragedoors.co.uk/#business",
    "name": "SmartLift Garage Doors",
    "alternateName": "SmartLift",
    "sameAs": ["https://maps.google.com/?cid=10431130324336682582"],
    "description": pageDescription || "Professional garage door repairs, servicing and automation specialists covering Berkshire, Hampshire and Surrey. 24/7 emergency service available.",
    "url": pageUrl || "https://smartliftgaragedoors.co.uk",
    "telephone": "+447836393815",
    "email": "office@smartliftgaragedoors.co.uk",
    "image": [
      "https://smartliftgaragedoors.co.uk/favicon.png",
      "https://smartliftgaragedoors.co.uk/og-image.png"
    ],
    "logo": "https://smartliftgaragedoors.co.uk/favicon.png",
    "priceRange": "££",
    "currenciesAccepted": "GBP",
    "paymentAccepted": "Cash, Credit Card, Debit Card, Bank Transfer",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Yateley",
      "addressRegion": "Hampshire",
      "postalCode": "GU46",
      "addressCountry": "GB"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "51.3402",
      "longitude": "-0.8186"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "08:00",
        "closes": "16:30"
      }
    ],
    "areaServed": [
      {
        "@type": "AdministrativeArea",
        "name": "Berkshire",
        "containedIn": {
          "@type": "Country",
          "name": "United Kingdom"
        }
      },
      {
        "@type": "AdministrativeArea",
        "name": "Hampshire",
        "containedIn": {
          "@type": "Country",
          "name": "United Kingdom"
        }
      },
      {
        "@type": "AdministrativeArea",
        "name": "Surrey",
        "containedIn": {
          "@type": "Country",
          "name": "United Kingdom"
        }
      }
    ],
    "knowsAbout": [
      "Garage Door Repair",
      "Garage Door Servicing",
      "Garage Door Automation",
      "Sectional Garage Doors",
      "Roller Garage Doors",
      "Up and Over Garage Doors",
      "Electric Garage Door Operators"
    ],
    "slogan": "Your Local Garage Door Specialists",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Garage Door Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Emergency Garage Door Repair",
            "description": "24/7 emergency garage door repair service. Emergency callouts are £189."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Garage Door Servicing",
            "description": "Annual maintenance and safety inspections for all garage door types"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Garage Door Automation",
            "description": "Smart garage door automation with WiFi, smartphone apps and voice control"
          }
        }
      ]
    }
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "SmartLift Garage Doors",
    "url": "https://smartliftgaragedoors.co.uk",
    "logo": "https://smartliftgaragedoors.co.uk/favicon.png",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+447836393815",
      "contactType": "customer service",
      "email": "office@smartliftgaragedoors.co.uk",
      "availableLanguage": "English",
      "areaServed": "GB"
    },
    "sameAs": ["https://maps.google.com/?cid=10431130324336682582"]
  };

  const breadcrumbSchema = pageName ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://smartliftgaragedoors.co.uk"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": pageName,
        "item": pageUrl
      }
    ]
  } : null;

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
};

interface ServiceSchemaProps {
  serviceName: string;
  serviceDescription: string;
  serviceUrl: string;
  areaServed?: string;
}

export const ServiceSchema = ({ serviceName, serviceDescription, serviceUrl, areaServed }: ServiceSchemaProps) => {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": serviceName,
    "description": serviceDescription,
    "url": serviceUrl,
    "provider": {
      "@type": "LocalBusiness",
      "name": "SmartLift Garage Doors",
      "telephone": "+447836393815",
      "email": "office@smartliftgaragedoors.co.uk"
    },
    "areaServed": areaServed ? {
      "@type": "AdministrativeArea",
      "name": areaServed
    } : [
      { "@type": "AdministrativeArea", "name": "Berkshire" },
      { "@type": "AdministrativeArea", "name": "Hampshire" },
      { "@type": "AdministrativeArea", "name": "Surrey" }
    ],
    "serviceType": serviceName
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(serviceSchema)}
      </script>
    </Helmet>
  );
};

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSchemaProps {
  faqs: FAQItem[];
}

export const FAQSchema = ({ faqs }: FAQSchemaProps) => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
    </Helmet>
  );
};

// Predefined FAQ sets for different pages
export const homeFAQs: FAQItem[] = [
  {
    question: "Do you offer 24/7 emergency garage door repairs?",
    answer: "Yes, SmartLift Garage Doors provides 24/7 emergency garage door repair services across Berkshire, Hampshire, and Surrey. Emergency callouts are £189, and we aim to arrive within 2 hours."
  },
  {
    question: "What areas do you cover for garage door repairs?",
    answer: "We cover Berkshire, Hampshire, and Surrey including towns such as Reading, Wokingham, Bracknell, Maidenhead, Southampton, Portsmouth, Winchester, Basingstoke, Guildford, Woking, Farnham, and Camberley."
  },
  {
    question: "Do you install new garage doors?",
    answer: "No, we specialise exclusively in garage door repairs, servicing, and automation. We do not install new garage doors, allowing us to focus on being experts in maintenance and repairs."
  },
  {
    question: "What types of garage doors can you repair?",
    answer: "We repair all types of garage doors including up & over, roller, sectional, side-hinged, and automated doors from all major manufacturers."
  },
  {
    question: "How much does a garage door repair cost?",
    answer: "Repair costs vary depending on the issue and parts required. We provide free quotes with no obligation. There is no call-out fee for standard booked visits. Emergency callouts are £189."
  }
];

export const repairsFAQs: FAQItem[] = [
  {
    question: "What garage door problems can you fix?",
    answer: "We fix all common garage door issues including broken springs, damaged cables, faulty motors, misaligned tracks, worn rollers, broken panels, and remote control problems."
  },
  {
    question: "How quickly can you repair my garage door?",
    answer: "For emergencies, we aim to attend the same day. For non-urgent repairs, we typically schedule appointments within 1-3 working days depending on your availability."
  },
  {
    question: "Do you carry spare parts?",
    answer: "Yes, our engineers carry a comprehensive range of common spare parts, allowing most repairs to be completed on the first visit."
  },
  {
    question: "Is there a warranty on repairs?",
    answer: "Yes, all our repair work comes with a warranty on both parts and labour, giving you peace of mind and confidence in our services."
  }
];

export const servicingFAQs: FAQItem[] = [
  {
    question: "How often should I service my garage door?",
    answer: "We recommend annual servicing for all garage doors. Regular maintenance extends the lifespan of your door, ensures safe operation, and helps prevent costly emergency repairs."
  },
  {
    question: "What does a garage door service include?",
    answer: "Our comprehensive service includes safety checks, lubrication of moving parts, tension adjustments, track alignment, motor inspection for automated doors, and a full operational test."
  },
  {
    question: "Can you service all brands of garage doors?",
    answer: "Yes, we service garage doors from all manufacturers including Hormann, Garador, Henderson, Cardale, Wessex, and many more."
  },
  {
    question: "Will servicing prevent future breakdowns?",
    answer: "Regular servicing significantly reduces the risk of unexpected breakdowns by identifying and addressing wear and potential issues before they become serious problems."
  }
];

export const automationFAQs: FAQItem[] = [
  {
    question: "Can you automate my existing manual garage door?",
    answer: "In most cases, yes. We can fit electric operators to most manual garage doors, allowing you to open and close your door with a remote control or smartphone app."
  },
  {
    question: "What automation brands do you work with?",
    answer: "We work with leading automation brands including Hormann, Marantec, Somfy, Nice, and other premium manufacturers to ensure reliable, long-lasting automation solutions."
  },
  {
    question: "Can I control my garage door from my phone?",
    answer: "Yes, we offer smart automation solutions with WiFi connectivity, allowing you to control your garage door via smartphone apps. Some systems also support voice control through Alexa or Google Home."
  },
  {
    question: "Is garage door automation safe?",
    answer: "Yes, modern automation systems include multiple safety features such as auto-reverse sensors, soft start/stop, and manual release mechanisms for power failures."
  }
];

export default LocalBusinessSchema;